import React, { useEffect, useState } from "react";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { portalApi } from "../lib/portalApi";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";

const KINDS = [
  ["budget_pdf",          "Budget PDF"],
  ["infrastructure_csv",  "Infrastructure Inventory CSV"],
  ["financial_csv",       "Financial Records CSV"],
  ["department_manual",   "Department Spending (manual)"],
  ["water_metrics",       "Water System Metrics"],
];

const Inner = () => {
  const { activeOrg } = usePortal();
  const [filename, setFilename] = useState("");
  const [kind, setKind] = useState("budget_pdf");
  const [size, setSize] = useState(0);
  const [notes, setNotes] = useState("");
  const [list, setList] = useState([]);

  const load = () => portalApi.listUploads().then(r => setList(r.uploads || []));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFilename(f.name); setSize(f.size);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!activeOrg) return toast.error("Select an organization in the header");
    if (!filename) return toast.error("Choose a file or enter a filename");
    try {
      await portalApi.uploadMeta({
        organization_id: activeOrg, filename, kind, size_bytes: size, notes: notes || null,
      });
      toast.success("Upload catalogued (metadata-only in demo mode)");
      setFilename(""); setNotes(""); setSize(0);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Data Uploads</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Catalog municipal records</h1>
        <p className="text-sm text-cream-dim mt-1">
          Demo mode stores only filename, kind, and size (tenant-isolated). Production
          deployments persist files in secure object storage.
        </p>
      </div>

      <form onSubmit={submit} className="glass p-6 grid md:grid-cols-2 gap-4" data-testid="upload-form">
        <div>
          <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Choose file</label>
          <input type="file" onChange={onFile}
            className="mt-1 block w-full text-sm text-cream-dim bg-navy-deep border border-gold-dim/40 rounded p-2 file:mr-3 file:px-3 file:py-1 file:bg-gold file:text-navy file:border-0 file:rounded"
            data-testid="upload-file" />
          <div className="text-[10px] text-steel mt-1">{filename ? `${filename} · ${size.toLocaleString()} bytes` : "No file selected"}</div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Kind</label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream" data-testid="upload-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Notes (optional)</label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="mt-1 bg-navy-deep border-gold-dim/40" data-testid="upload-notes" />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button className="btn-gold h-11 px-6" data-testid="upload-submit">
            <UploadCloud size={14} className="mr-2" /> Catalog Upload
          </Button>
        </div>
      </form>

      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="uploads-table">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
            <tr className="border-b border-gold-dim/20">
              <th className="text-left p-4">Created</th>
              <th className="text-left p-4">Filename</th>
              <th className="text-left p-4">Kind</th>
              <th className="text-right p-4">Size</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-steel">No uploads yet.</td></tr>
            ) : list.map(u => (
              <tr key={u.id} className="border-b border-gold-dim/10">
                <td className="p-4 text-cream-dim">{new Date(u.created_at).toLocaleString()}</td>
                <td className="p-4 text-gold font-mono text-xs">{u.filename}</td>
                <td className="p-4 text-cream-dim">{u.kind}</td>
                <td className="p-4 text-right text-cream">{(u.size_bytes || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PortalUploads = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalUploads;
